import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface SurfSection {
  title: string;
  description: string;
  image_urls: string[];
  alt_keyword: string;
}

interface Props {
  section: SurfSection;
  reverse?: boolean;
}

const SurfSectionSlider = ({ section, reverse }: Props) => {
  const images = section.image_urls?.length ? section.image_urls : [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % images.length), 5500);
    return () => clearInterval(t);
  }, [images.length]);

  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);

  return (
    <section className="py-12 md:py-16 bg-white border-t border-gray-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-8 items-center">
        <div className={reverse ? "md:order-2" : ""}>
          <h2 className="text-2xl md:text-3xl font-bold text-primary">{section.title}</h2>
          <p className="text-gray-700 mt-4 leading-relaxed whitespace-pre-line">{section.description}</p>
        </div>

        <div className={`relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 shadow-lg ${reverse ? "md:order-1" : ""}`}>
          {images.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center text-gray-700 text-sm">
              Aucune image
            </div>
          ) : (
            <>
              {images.map((url, i) => (
                <img
                  key={url + i}
                  src={url}
                  alt={`${section.alt_keyword} — photo ${i + 1}`}
                  title={section.title}
                  loading="lazy"
                  draggable={false}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    i === index ? "opacity-100" : "opacity-0"
                  } pointer-events-none select-none cursor-default`}
                />
              ))}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Image précédente"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-primary flex items-center justify-center shadow-md transition"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Image suivante"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-primary flex items-center justify-center shadow-md transition"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setIndex(i)}
                        aria-label={`Aller à l'image ${i + 1}`}
                        className={`w-2 h-2 rounded-full transition ${
                          i === index ? "bg-white w-5" : "bg-white/60 hover:bg-white/80"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default SurfSectionSlider;